import { useState } from "react";
import { Header } from "@/components/navigation/Header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Play, Code, MessageSquare, FileText, Image, Download, Users, Server, Headphones } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

type DemoType = "document-summarization" | "code-translation" | "multiagent-qna" | "pdf-to-podcast" | "enterprise-rag-chatbot";

interface Demo {
  id?: DemoType;
  title: string;
  description: string;
  category: string;
  icon: typeof MessageSquare;
  status: "live" | "beta" | "coming-soon";
  complexity: string;
  hasDownload?: boolean;
}

interface TerraformFormState {
  idracEndpoint: string;
  idracUser: string;
  idracPassword: string;
  ubuntuUsername: string;
  ubuntuPassword: string;
  ubuntuHostname: string;
  configMode: "basic" | "advanced";
  useDhcp: boolean;
  staticIp: string;
  staticNetmask: string;
  staticGateway: string;
  staticDns: string;
  sshKeys: string;
  idracSslInsecure: boolean;
  moduleVersion: string;
}

const Downloads = () => {
  const { toast } = useToast();
  
  // Docker Compose modal state
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [activeDemoType, setActiveDemoType] = useState<DemoType | null>(null);
  
  // Docker Compose form state
  const [inferenceUrl, setInferenceUrl] = useState("");
  const [model, setModel] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [embeddingModel, setEmbeddingModel] = useState("bge-base-en-v1.5");
  
  // Keycloak state
  const [useKeycloak, setUseKeycloak] = useState(false);
  const [keycloakClientId, setKeycloakClientId] = useState("");
  const [keycloakClientSecret, setKeycloakClientSecret] = useState("");
  const [keycloakRealm, setKeycloakRealm] = useState("master");

  // TTS-specific state for PDF-to-Podcast
  const [useSameEndpointForTts, setUseSameEndpointForTts] = useState(true);
  const [ttsApiKey, setTtsApiKey] = useState("");
  const [ttsBaseUrl, setTtsBaseUrl] = useState("https://api.openai.com/v1");
  const [ttsModel, setTtsModel] = useState("tts-1-hd");
  const [hostVoice, setHostVoice] = useState("alloy");
  const [guestVoice, setGuestVoice] = useState("nova");
  // TTS Keycloak (optional, separate from LLM Keycloak)
  const [useTtsKeycloak, setUseTtsKeycloak] = useState(false);
  const [ttsKeycloakClientId, setTtsKeycloakClientId] = useState("");
  const [ttsKeycloakClientSecret, setTtsKeycloakClientSecret] = useState("");
  const [ttsKeycloakAudience, setTtsKeycloakAudience] = useState("");

  // RAG Chatbot embeddings configuration
  const [useLocalEmbeddings, setUseLocalEmbeddings] = useState(false);
  const [embeddingsBaseUrl, setEmbeddingsBaseUrl] = useState("");

  // Terraform modal state
  const [terraformModalOpen, setTerraformModalOpen] = useState(false);
  const [terraformForm, setTerraformForm] = useState<TerraformFormState>({
    idracEndpoint: "",
    idracUser: "root",
    idracPassword: "",
    ubuntuUsername: "ubuntu",
    ubuntuPassword: "",
    ubuntuHostname: "ubuntu-server",
    configMode: "basic",
    useDhcp: true,
    staticIp: "",
    staticNetmask: "24",
    staticGateway: "",
    staticDns: "",
    sshKeys: "",
    idracSslInsecure: true,
    moduleVersion: "0.1.0",
  });
  const [terraformErrors, setTerraformErrors] = useState<Record<string, string>>({});

  const demos: Demo[] = [
    {
      id: "enterprise-rag-chatbot",
      title: "Enterprise RAG Chatbot",
      description: "Intelligent customer service chatbot with RAG and context awareness",
      category: "Conversational AI",
      icon: MessageSquare,
      status: "live",
      complexity: "Medium",
      hasDownload: true
    },
    {
      id: "code-translation",
      title: "Code Translation",
      description: "AI-powered code translation between programming languages",
      category: "Developer Tools",
      icon: Code,
      status: "live", 
      complexity: "High",
      hasDownload: true
    },
    {
      id: "document-summarization",
      title: "Document Summarization",
      description: "AI-powered document summarization with customizable deployment",
      category: "Document Processing",
      icon: FileText,
      status: "live",
      complexity: "Medium",
      hasDownload: true
    },
    {
      id: "multiagent-qna",
      title: "Multi-Agent Q&A",
      description: "Multi-agent question and answering system with RAG and embeddings",
      category: "Conversational AI",
      icon: Users,
      status: "live",
      complexity: "High",
      hasDownload: true
    },
    {
      id: "pdf-to-podcast",
      title: "PDF to Podcast",
      description: "Convert PDF documents into engaging podcast-style audio conversations",
      category: "Audio Generation",
      icon: Headphones,
      status: "live",
      complexity: "High",
      hasDownload: true
    },
    {
      title: "Document Analysis",
      description: "Extract insights from technical documentation",
      category: "Document Processing",
      icon: FileText,
      status: "beta",
      complexity: "Medium"
    },
    {
      title: "Visual Q&A",
      description: "Answer questions about images and diagrams",
      category: "Computer Vision",
      icon: Image,
      status: "coming-soon",
      complexity: "High"
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "live": return "bg-green-500/20 text-green-700 dark:text-green-400";
      case "beta": return "bg-yellow-500/20 text-yellow-700 dark:text-yellow-400";
      case "coming-soon": return "bg-blue-500/20 text-blue-700 dark:text-blue-400";
      default: return "bg-muted text-muted-foreground";
    }
  };

  const generateDockerCompose = (demoType: DemoType) => {
    const keycloakEnv = useKeycloak 
      ? `KEYCLOAK_CLIENT_ID: "${keycloakClientId}"
      KEYCLOAK_CLIENT_SECRET: "${keycloakClientSecret}"
      KEYCLOAK_REALM: "${keycloakRealm}"`
      : `KEYCLOAK_CLIENT_ID: ""
      KEYCLOAK_CLIENT_SECRET: ""
      KEYCLOAK_REALM: "master"`;

    if (demoType === "code-translation") {
      return `services:
  backend:
    image: vectorpathconsulting/code-translation-backend:latest
    container_name: code-translation-backend
    environment:
      # Required for OpenAI compatible endpoint
      BASE_URL: "${inferenceUrl}"
      INFERENCE_API_KEY: "${apiKey}"
      INFERENCE_MODEL_NAME: "${model}"

      # Optional Keycloak (only used if all are set)
      ${keycloakEnv}

      # Optional tuning and limits
      LLM_TEMPERATURE: "0.2"
      LLM_MAX_TOKENS: "4096"
      MAX_CODE_LENGTH: "10000"
      MAX_FILE_SIZE: "10485760"
    ports:
      - "5001:5001"
    networks:
      - appnet
    restart: unless-stopped

  frontend:
    image: vectorpathconsulting/code-translation-frontend:latest
    container_name: code-translation-frontend
    depends_on:
      - backend
    ports:
      - "3000:80"
    networks:
      - appnet
    restart: unless-stopped

  info:
    image: alpine:latest
    container_name: code-translation-info
    command: >
      sh -c '
      echo "";
      echo "==============================================";
      echo "   Inference Blueprint: Code Translation";
      echo "----------------------------------------------";
      echo "   Open your browser to: http://localhost:3000";
      echo "==============================================";
      echo "";
      sleep 8;
      '
    networks:
      - appnet
    restart: "no"

networks:
  appnet:
    driver: bridge
`;
    }

    if (demoType === "multiagent-qna") {
      return `services:
  backend:
    image: vectorpathconsulting/multiagent-qna-backend:latest
    container_name: multiagent-qna-backend
    environment:
      # Required: the only thing a user absolutely must provide
      INFERENCE_API_KEY: "${apiKey}"

      # Reasonable defaults for your hosted gateway
      BASE_URL: "${inferenceUrl}"

      # Default model endpoints and names on the gateway
      EMBEDDING_MODEL_NAME: "${embeddingModel}"
      INFERENCE_MODEL_NAME: "${model}"

      # Optional Keycloak. Left blank means direct API key auth is used.
      ${keycloakEnv}

      # Optional tuning with safe defaults
      LLM_TEMPERATURE: "0.2"
      LLM_MAX_TOKENS: "4096"

    ports:
      - "8002:5001"
    networks:
      - appnet
    restart: unless-stopped

  frontend:
    image: vectorpathconsulting/multiagent-qna-frontend:latest
    container_name: multiagent-qna-frontend
    depends_on:
      - backend
    ports:
      - "8082:3000"    # host:container
    networks:
      - appnet
    restart: unless-stopped

  info:
    image: alpine:latest
    container_name: multiagent-qna-info
    command: >
      sh -c "
      echo '';
      echo '==============================================';
      echo '  Inference Blueprint: Multi-Agent Q&A';
      echo '----------------------------------------------';
      echo '  Open your browser to: http://localhost:8082';
      echo '==============================================';
      echo '';
      sleep 10;
      "
    networks:
      - appnet
    restart: "no"

networks:
  appnet:
    driver: bridge
`;
    }

    if (demoType === "pdf-to-podcast") {
      // LLM Keycloak settings
      const llmKeycloakEnv = useKeycloak 
        ? `KEYCLOAK_CLIENT_ID: "${keycloakClientId}"
      KEYCLOAK_CLIENT_SECRET: "${keycloakClientSecret}"
      KEYCLOAK_REALM: "${keycloakRealm}"`
        : `KEYCLOAK_CLIENT_ID: ""
      KEYCLOAK_CLIENT_SECRET: ""
      KEYCLOAK_REALM: "master"`;

      // Determine TTS endpoint values (use LLM values if synced)
      const effectiveTtsBaseUrl = useSameEndpointForTts ? inferenceUrl : ttsBaseUrl;
      const effectiveTtsApiKey = useSameEndpointForTts ? apiKey : ttsApiKey;

      // TTS Keycloak settings (only shown when using separate endpoints)
      const ttsKeycloakEnv = (!useSameEndpointForTts && useTtsKeycloak)
        ? `KEYCLOAK_CLIENT_ID: "${ttsKeycloakClientId}"
      KEYCLOAK_CLIENT_SECRET: "${ttsKeycloakClientSecret}"
      KEYCLOAK_AUDIENCE: "${ttsKeycloakAudience}"`
        : `KEYCLOAK_CLIENT_ID: ""
      KEYCLOAK_CLIENT_SECRET: ""
      KEYCLOAK_AUDIENCE: ""`;

      return `version: "3.9"

services:
  # Extracts and cleans text from PDF uploads
  pdf-service:
    image: vectorpathconsulting/pdf-podcast-pdf-service:latest
    container_name: pdf-podcast-pdf-service
    ports:
      - "8001:8001"
    networks:
      - appnet
    restart: unless-stopped

  # Uses a generic OpenAI compatible LLM endpoint for script generation
  llm-service:
    image: vectorpathconsulting/pdf-podcast-llm-service:latest
    container_name: pdf-podcast-llm-service
    environment:
      # LLM gateway (can be local or public, for example https://api.openai.com)
      BASE_URL: "${inferenceUrl}"
      INFERENCE_API_KEY: "${apiKey}"

      # Optional Keycloak for LLM
      ${llmKeycloakEnv}

      # Model name as exposed by the gateway
      INFERENCE_MODEL_NAME: "${model}"
    ports:
      - "8002:8002"
    networks:
      - appnet
    restart: unless-stopped

  # Uses a generic OpenAI compatible TTS endpoint to turn script into audio
  tts-service:
    image: vectorpathconsulting/pdf-podcast-tts-service:latest
    container_name: pdf-podcast-tts-service
    environment:
      # TTS gateway (can be local or public, for example https://api.openai.com)
      TTS_BASE_URL: "${effectiveTtsBaseUrl}"
      TTS_API_KEY: "${effectiveTtsApiKey}"

      # Optional Keycloak for TTS (shared field names)
      ${ttsKeycloakEnv}

      # TTS model and voice defaults (as your gateway expects)
      TTS_MODEL: "${ttsModel}"
      DEFAULT_HOST_VOICE: "${hostVoice}"
      DEFAULT_GUEST_VOICE: "${guestVoice}"

      # Output directory inside the container
      OUTPUT_DIR: "/app/static/audio"
    ports:
      - "8003:8003"
    networks:
      - appnet
    restart: unless-stopped

  # Backend gateway that wires PDF, LLM and TTS together
  backend:
    image: vectorpathconsulting/pdf-podcast-backend:latest
    container_name: pdf-podcast-backend
    environment:
      PDF_SERVICE_URL: "http://pdf-service:8001"
      LLM_SERVICE_URL: "http://llm-service:8002"
      TTS_SERVICE_URL: "http://tts-service:8003"
    depends_on:
      - pdf-service
      - llm-service
      - tts-service
    ports:
      - "8000:8000"
    networks:
      - appnet
    restart: unless-stopped

  # Frontend that users open in the browser
  frontend:
    image: vectorpathconsulting/pdf-podcast-frontend:latest
    container_name: pdf-podcast-frontend
    depends_on:
      - backend
    ports:
      - "8083:3000"    # container listens on 3000; expose as 8083
    networks:
      - appnet
    restart: unless-stopped

  # Simple helper that prints connection info then exits
  info:
    image: alpine:latest
    container_name: pdf-podcast-info
    command: >
      sh -c "
      echo '';
      echo '==============================================';
      echo '   Inference Blueprint: PDF to Podcast';
      echo '----------------------------------------------';
      echo '   Open your browser to: http://localhost:8083';
      echo '==============================================';
      echo '';
      sleep 10;
      "
    networks:
      - appnet

networks:
  appnet:
    driver: bridge
`;
    }

    if (demoType === "enterprise-rag-chatbot") {
      if (useLocalEmbeddings) {
        // Local embeddings template
        return `version: "3.9"

services:
  rag-backend:
    image: vectorpathconsulting/rag-chat-backend:latest
    container_name: rag-chat-backend
    ports:
      - "5001:5001"
    environment:
      # Inference gateway (remote LLM)
      BASE_URL: "${inferenceUrl}"

      # Local embeddings endpoint inside the compose network
      EMBEDDINGS_BASE_URL: "http://local-embeddings:8080"

      # Auth for inference gateway
      INFERENCE_API_KEY: "${apiKey}"

      # Model hints
      INFERENCE_MODEL_NAME: "${model}"
      EMBEDDING_MODEL_NAME: "${embeddingModel}"
    networks:
      - appnet
    restart: unless-stopped
    depends_on:
      - local-embeddings

  rag-frontend:
    image: vectorpathconsulting/rag-chat-frontend:latest
    container_name: rag-chat-frontend
    depends_on:
      - rag-backend
    environment:
      VITE_API_URL: "/api"
      BACKEND_PROXY_URL: "http://rag-backend:5001"
    ports:
      - "8084:3000"
    networks:
      - appnet
    restart: unless-stopped

  # Local embeddings service, built into your published image
  local-embeddings:
    image: vectorpathconsulting/rag-local-embeddings:latest
    container_name: rag-local-embeddings
    environment:
      MODEL_NAME: "${embeddingModel}"
    ports:
      # Host port only for testing. Backend talks to service by name on 8080.
      - "8088:8080"
    networks:
      - appnet
    restart: unless-stopped
    healthcheck:
      test: ["CMD", "curl", "-f", "http://localhost:8080/health"]
      interval: 10s
      timeout: 5s
      retries: 5
      start_period: 10s

  info:
    image: alpine:latest
    container_name: rag-chat-info
    command: >
      sh -c "
      echo '';
      echo '==============================================';
      echo '   Inference Blueprint: RAG Chatbot';
      echo '----------------------------------------------';
      echo '   API:      http://localhost:5001';
      echo '   Frontend: http://localhost:8084';
      echo '   Local embeddings: http://localhost:8088';
      echo '==============================================';
      echo '';
      sleep 10;
      "
    networks:
      - appnet

networks:
  appnet:
    driver: bridge
`;
      } else {
        // Remote embeddings template
        return `version: "3.9"

services:
  rag-backend:
    image: vectorpathconsulting/rag-chat-backend:latest
    container_name: rag-chat-backend
    ports:
      - "5001:5001"
    environment:
      # Inference gateway
      BASE_URL: "${inferenceUrl}"

      # Remote embeddings endpoint
      EMBEDDINGS_BASE_URL: "${embeddingsBaseUrl}"

      # Auth
      INFERENCE_API_KEY: "${apiKey}"

      # Model hints
      INFERENCE_MODEL_NAME: "${model}"
      EMBEDDING_MODEL_NAME: "${embeddingModel}"
    networks:
      - appnet
    restart: unless-stopped

  rag-frontend:
    image: vectorpathconsulting/rag-chat-frontend:latest
    container_name: rag-chat-frontend
    depends_on:
      - rag-backend
    environment:
      # Browser hits /api. Frontend proxies to backend.
      VITE_API_URL: "/api"
      BACKEND_PROXY_URL: "http://rag-backend:5001"
    ports:
      - "8084:3000"
    networks:
      - appnet
    restart: unless-stopped

  info:
    image: alpine:latest
    container_name: rag-chat-info
    command: >
      sh -c "
      echo '';
      echo '==============================================';
      echo '   Inference Blueprint: RAG Chatbot';
      echo '----------------------------------------------';
      echo '   API:      http://localhost:5001';
      echo '   Frontend: http://localhost:8084';
      echo '==============================================';
      echo '';
      sleep 10;
      "
    networks:
      - appnet

networks:
  appnet:
    driver: bridge
`;
      }
    }

    // Document Summarization
    return `services:
  backend:
    image: vectorpathconsulting/doc-summarization-backend:latest
    container_name: doc-summarization-backend
    environment:
      # Required (OpenAI-compatible endpoint)
      BASE_URL: "${inferenceUrl}"
      INFERENCE_API_KEY: "${apiKey}"
      INFERENCE_MODEL_NAME: "${model}"

      # Optional Keycloak (only used if all values provided)
      ${keycloakEnv}

      # Optional tuning
      LLM_TEMPERATURE: "0.7"
      LLM_MAX_TOKENS: "2000"
    ports:
      - "8000:8000"
    networks:
      - appnet
    restart: unless-stopped

  frontend:
    image: vectorpathconsulting/doc-summarization-frontend:latest
    container_name: doc-summarization-frontend
    depends_on:
      - backend
    ports:
      - "8080:80"
    networks:
      - appnet
    restart: unless-stopped

  info:
    image: alpine:latest
    container_name: doc-sum-info
    command: >
      sh -c '
      echo "";
      echo "==============================================";
      echo "   Inference Blueprint: Document Summarization";
      echo "----------------------------------------------";
      echo "   Open your browser to: http://localhost:8080";
      echo "==============================================";
      echo "";
      sleep 8;
      '
    networks:
      - appnet
    restart: "no"

networks:
  appnet:
    driver: bridge
`;
  };

  const handleDownload = () => {
    if (!activeDemoType) return;
    const yaml = generateDockerCompose(activeDemoType);
    const blob = new Blob([yaml], { type: "text/yaml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "docker-compose.yml";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setDownloadModalOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setInferenceUrl("");
    setModel("");
    setApiKey("");
    setEmbeddingModel("bge-base-en-v1.5");
    setUseKeycloak(false);
    setKeycloakClientId("");
    setKeycloakClientSecret("");
    setKeycloakRealm("master");
    // TTS fields
    setUseSameEndpointForTts(true);
    setTtsApiKey("");
    setTtsBaseUrl("https://api.openai.com/v1");
    setTtsModel("tts-1-hd");
    setHostVoice("alloy");
    setGuestVoice("nova");
    setUseTtsKeycloak(false);
    setTtsKeycloakClientId("");
    setTtsKeycloakClientSecret("");
    setTtsKeycloakAudience("");
    // RAG embeddings fields
    setUseLocalEmbeddings(false);
    setEmbeddingsBaseUrl("");
    setActiveDemoType(null);
  };

  const copyLlmToEmbeddings = () => {
    setEmbeddingsBaseUrl(inferenceUrl);
  };

  const openDownloadModal = (demoType: DemoType) => {
    resetForm();
    setActiveDemoType(demoType);
    if (demoType === "multiagent-qna") {
      setModel("meta-llama/Llama-3.1-8B-Instruct");
      setEmbeddingModel("bge-base-en-v1.5");
    }
    if (demoType === "enterprise-rag-chatbot") {
      setEmbeddingModel("BAAI/bge-base-en-v1.5");
    }
    setDownloadModalOpen(true);
  };

  const getModalTitle = () => {
    switch (activeDemoType) {
      case "code-translation": return "Configure Code Translation Deployment";
      case "document-summarization": return "Configure Document Summarization Deployment";
      case "multiagent-qna": return "Configure Multi-Agent Q&A Deployment";
      case "pdf-to-podcast": return "Configure PDF to Podcast Deployment";
      case "enterprise-rag-chatbot": return "Configure Enterprise RAG Chatbot Deployment";
      default: return "Configure Deployment";
    }
  };

  // Terraform form handlers
  const updateTerraformForm = (field: keyof TerraformFormState, value: string | boolean) => {
    setTerraformForm(prev => ({ ...prev, [field]: value }));
    // Clear error when field is updated
    if (terraformErrors[field]) {
      setTerraformErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const resetTerraformForm = () => {
    setTerraformForm({
      idracEndpoint: "",
      idracUser: "root",
      idracPassword: "",
      ubuntuUsername: "ubuntu",
      ubuntuPassword: "",
      ubuntuHostname: "ubuntu-server",
      configMode: "basic",
      useDhcp: true,
      staticIp: "",
      staticNetmask: "24",
      staticGateway: "",
      staticDns: "",
      sshKeys: "",
      idracSslInsecure: true,
      moduleVersion: "0.1.0",
    });
    setTerraformErrors({});
  };

  const validateTerraformForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!terraformForm.idracEndpoint) errors.idracEndpoint = "iDRAC endpoint is required";
    if (!terraformForm.idracPassword) errors.idracPassword = "iDRAC password is required";
    if (!terraformForm.ubuntuPassword) errors.ubuntuPassword = "Ubuntu password is required";
    
    if (terraformForm.configMode === "advanced" && !terraformForm.useDhcp) {
      if (!terraformForm.staticIp) errors.staticIp = "Static IP must be set when using static networking";
      if (!terraformForm.staticGateway) errors.staticGateway = "Gateway must be set when using static networking";
    }
    
    setTerraformErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const generateTerraformFile = (): string => {
    const sshKeysArray = terraformForm.sshKeys
      .split('\n')
      .map(k => k.trim())
      .filter(k => k.length > 0);
    
    const sshKeysBlock = sshKeysArray.length > 0
      ? sshKeysArray.map(k => `    "${k}"`).join(',\n')
      : '';
    
    const dnsArray = terraformForm.staticDns
      .split(',')
      .map(d => d.trim())
      .filter(d => d.length > 0);
    
    const dnsBlock = dnsArray.map(d => `"${d}"`).join(', ');

    return `terraform {
  required_version = ">= 1.4"

  required_providers {
    redfish = {
      source  = "dell/redfish"
      version = "1.6.0"
    }
  }
}

module "ubuntu_genai" {
  source  = "VPC-DevAdmin/ubuntu-genai/idrac"
  version = "${terraformForm.moduleVersion}"

  idrac_endpoint  = "${terraformForm.idracEndpoint}"
  idrac_user      = "${terraformForm.idracUser}"
  idrac_password  = "${terraformForm.idracPassword}"

  ubuntu_username = "${terraformForm.ubuntuUsername}"
  ubuntu_password = "${terraformForm.ubuntuPassword}"
  ubuntu_hostname = "${terraformForm.ubuntuHostname}"

  ssh_keys = [
${sshKeysBlock}
  ]

  use_dhcp       = ${terraformForm.useDhcp}
  static_ip      = "${terraformForm.staticIp}"
  static_netmask = "${terraformForm.staticNetmask}"
  static_gateway = "${terraformForm.staticGateway}"
  static_dns     = [${dnsBlock}]

  idrac_ssl_insecure = ${terraformForm.idracSslInsecure}
}
`;
  };

  const handleTerraformDownload = () => {
    if (!validateTerraformForm()) return;
    
    const content = generateTerraformFile();
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "main.tf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast({
      title: "main.tf downloaded",
      description: "Run terraform init and terraform apply in that folder.",
    });
    
    setTerraformModalOpen(false);
    resetTerraformForm();
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-8 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl font-bold mb-2">Downloads</h1>
              <p className="text-muted-foreground text-lg">
                Explore AI tools and infrastructure automation
              </p>
            </div>

            {/* Blueprints Section */}
            <section className="mb-12">
              <div className="mb-6">
                <h2 className="text-2xl font-semibold mb-1">Blueprints</h2>
                <p className="text-muted-foreground">Docker Compose templates for AI applications</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {demos.map((demo) => {
                  const IconComponent = demo.icon;
                  return (
                    <Card key={demo.title} className="hover:shadow-glow transition-smooth">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                              <IconComponent className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                              <CardTitle className="text-lg">{demo.title}</CardTitle>
                              <Badge variant="secondary" className="mt-1">
                                {demo.category}
                              </Badge>
                            </div>
                          </div>
                          <Badge className={getStatusColor(demo.status)}>
                            {demo.status.replace("-", " ")}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <CardDescription className="mb-4">
                          {demo.description}
                        </CardDescription>
                        <div className="flex items-center justify-between">
                          <div className="text-sm text-muted-foreground">
                            Complexity: <span className="font-medium">{demo.complexity}</span>
                          </div>
                          <div className="flex gap-2">
                            {demo.hasDownload && demo.id && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="gap-2"
                                onClick={() => openDownloadModal(demo.id!)}
                              >
                                <Download className="h-4 w-4" />
                                Download
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              disabled={demo.status === "coming-soon"}
                              className="gap-2"
                            >
                              <Play className="h-4 w-4" />
                              Try Demo
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </section>

            {/* Server Setup Section */}
            <section>
              <div className="mb-6">
                <h2 className="text-2xl font-semibold mb-1">Server Setup</h2>
                <p className="text-muted-foreground">Terraform modules and infrastructure automation</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Card className="hover:shadow-glow transition-smooth">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Server className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">Bare Metal to API Endpoint Installer</CardTitle>
                          <Badge variant="secondary" className="mt-1">Ubuntu</Badge>
                        </div>
                      </div>
                      <Badge className="bg-green-500/20 text-green-700 dark:text-green-400">live</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="mb-4">
                      Collect iDRAC and Ubuntu settings, then download a ready to run main.tf that uses the public module VPC-DevAdmin/ubuntu-genai/idrac.
                    </CardDescription>
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-muted-foreground">
                        Complexity: <span className="font-medium">Medium</span>
                      </div>
                      <Button 
                        size="sm" 
                        className="gap-2"
                        onClick={() => setTerraformModalOpen(true)}
                      >
                        <Download className="h-4 w-4" />
                        Generate main.tf
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </section>
          </div>
        </div>
      </main>

      {/* Docker Compose Download Configuration Modal */}
      <Dialog open={downloadModalOpen} onOpenChange={setDownloadModalOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{getModalTitle()}</DialogTitle>
            <DialogDescription>
              Enter your infrastructure details to generate a docker-compose.yml file. 
              Download it to a folder of your choice and run <code className="bg-muted px-1 rounded">docker compose up</code> to start.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* LLM Service Section Header for PDF-to-Podcast */}
            {activeDemoType === "pdf-to-podcast" && (
              <div className="pb-2 border-b">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  LLM Service (Script Generation)
                </h3>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="inferenceUrl">Inference Server URL *</Label>
              <Input 
                id="inferenceUrl" 
                placeholder="https://my-gateway.example.com" 
                value={inferenceUrl}
                onChange={(e) => setInferenceUrl(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="model">
                {activeDemoType === "enterprise-rag-chatbot" ? "Inference Model Name *" : "Model Name *"}
              </Label>
              <Input 
                id="model" 
                placeholder={activeDemoType === "pdf-to-podcast" ? "deepseek-ai/DeepSeek-R1-Distill-Qwen-32B" : "meta-llama/Llama-3.1-8B-Instruct"}
                value={model}
                onChange={(e) => setModel(e.target.value)}
              />
              {activeDemoType === "pdf-to-podcast" && (
                <p className="text-xs text-muted-foreground">Model name as exposed by the LLM gateway</p>
              )}
              {activeDemoType === "enterprise-rag-chatbot" && (
                <p className="text-xs text-muted-foreground">Should match what your gateway exposes under /v1/models</p>
              )}
            </div>

            {activeDemoType === "multiagent-qna" && (
              <div className="space-y-2">
                <Label htmlFor="embeddingModel">Embedding Model Name *</Label>
                <Input 
                  id="embeddingModel" 
                  placeholder="bge-base-en-v1.5" 
                  value={embeddingModel}
                  onChange={(e) => setEmbeddingModel(e.target.value)}
                />
              </div>
            )}

            {/* RAG Chatbot Embeddings Configuration */}
            {activeDemoType === "enterprise-rag-chatbot" && (
              <>
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="space-y-0.5">
                    <Label htmlFor="useLocalEmbeddings">Use Local Embeddings</Label>
                    <p className="text-sm text-muted-foreground">
                      Run embeddings locally instead of calling a remote endpoint
                    </p>
                  </div>
                  <Switch
                    id="useLocalEmbeddings"
                    checked={useLocalEmbeddings}
                    onCheckedChange={setUseLocalEmbeddings}
                  />
                </div>

                {/* Remote Embeddings URL - only when NOT using local */}
                {!useLocalEmbeddings && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="embeddingsBaseUrl">Embeddings Base URL *</Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={copyLlmToEmbeddings}
                        className="text-xs h-6"
                      >
                        Copy from LLM
                      </Button>
                    </div>
                    <Input 
                      id="embeddingsBaseUrl" 
                      placeholder="https://my-embed-gw.example.com"
                      value={embeddingsBaseUrl}
                      onChange={(e) => setEmbeddingsBaseUrl(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Remote OpenAI-compatible embeddings endpoint
                    </p>
                  </div>
                )}

                {/* Embedding Model Name - under embeddings section */}
                <div className="space-y-2">
                  <Label htmlFor="embeddingModel">Embedding Model Name *</Label>
                  <Input 
                    id="embeddingModel" 
                    placeholder="bge-base-en-v1.5" 
                    value={embeddingModel}
                    onChange={(e) => setEmbeddingModel(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">e.g. BAAI/bge-base-en-v1.5</p>
                </div>
              </>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="apiKey">API Key *</Label>
              <Input 
                id="apiKey" 
                type="password"
                placeholder="Enter your API key"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
              />
            </div>

            <div className="flex items-center justify-between pt-2 border-t">
              <div className="space-y-0.5">
                <Label htmlFor="useKeycloak">Use Keycloak Authentication</Label>
                <p className="text-sm text-muted-foreground">Optional: Enable for enterprise SSO</p>
              </div>
              <Switch
                id="useKeycloak"
                checked={useKeycloak}
                onCheckedChange={setUseKeycloak}
              />
            </div>

            {useKeycloak && (
              <div className="space-y-4 pt-2">
                <div className="space-y-2">
                  <Label htmlFor="keycloakClientId">Keycloak Client ID</Label>
                  <Input 
                    id="keycloakClientId" 
                    placeholder="my-client-id"
                    value={keycloakClientId}
                    onChange={(e) => setKeycloakClientId(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="keycloakClientSecret">Keycloak Client Secret</Label>
                  <Input 
                    id="keycloakClientSecret" 
                    type="password"
                    placeholder="Enter client secret"
                    value={keycloakClientSecret}
                    onChange={(e) => setKeycloakClientSecret(e.target.value)}
                  />
                </div>
                {activeDemoType === "enterprise-rag-chatbot" ? (
                  <div className="space-y-2">
                    <Label htmlFor="keycloakRealm">Keycloak Token URL</Label>
                    <Input 
                      id="keycloakRealm" 
                      placeholder="https://keycloak.example.com/realms/master/protocol/openid-connect/token"
                      value={keycloakRealm}
                      onChange={(e) => setKeycloakRealm(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">Full URL to the Keycloak token endpoint</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="keycloakRealm">Keycloak Realm</Label>
                    <Input 
                      id="keycloakRealm" 
                      placeholder="master"
                      value={keycloakRealm}
                      onChange={(e) => setKeycloakRealm(e.target.value)}
                    />
                  </div>
                )}
              </div>
            )}

            {/* TTS Service Section - only for pdf-to-podcast */}
            {activeDemoType === "pdf-to-podcast" && (
              <>
                <div className="pt-4 pb-2 border-t">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                    TTS Service (Text to Speech)
                  </h3>
                </div>

                {/* Toggle: Use same endpoint for TTS */}
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="useSameEndpointForTts">Use same endpoint for TTS</Label>
                    <p className="text-sm text-muted-foreground">TTS will use the LLM gateway URL and API key</p>
                  </div>
                  <Switch
                    id="useSameEndpointForTts"
                    checked={useSameEndpointForTts}
                    onCheckedChange={setUseSameEndpointForTts}
                  />
                </div>

                {/* Separate TTS endpoint fields - only show when not synced */}
                {!useSameEndpointForTts && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="ttsBaseUrl">TTS Base URL *</Label>
                      <Input 
                        id="ttsBaseUrl" 
                        placeholder="https://api.openai.com/v1"
                        value={ttsBaseUrl}
                        onChange={(e) => setTtsBaseUrl(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">OpenAI-compatible TTS endpoint</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="ttsApiKey">TTS API Key *</Label>
                      <Input 
                        id="ttsApiKey" 
                        type="password"
                        placeholder="Enter your TTS API key"
                        value={ttsApiKey}
                        onChange={(e) => setTtsApiKey(e.target.value)}
                      />
                    </div>

                    {/* TTS Keycloak - only when using separate endpoints */}
                    <div className="flex items-center justify-between pt-2 border-t">
                      <div className="space-y-0.5">
                        <Label htmlFor="useTtsKeycloak">Use Keycloak for TTS</Label>
                        <p className="text-sm text-muted-foreground">Optional: Enable for enterprise SSO</p>
                      </div>
                      <Switch
                        id="useTtsKeycloak"
                        checked={useTtsKeycloak}
                        onCheckedChange={setUseTtsKeycloak}
                      />
                    </div>

                    {useTtsKeycloak && (
                      <div className="space-y-4 pt-2">
                        <div className="space-y-2">
                          <Label htmlFor="ttsKeycloakClientId">TTS Keycloak Client ID</Label>
                          <Input 
                            id="ttsKeycloakClientId" 
                            placeholder="my-tts-client-id"
                            value={ttsKeycloakClientId}
                            onChange={(e) => setTtsKeycloakClientId(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="ttsKeycloakClientSecret">TTS Keycloak Client Secret</Label>
                          <Input 
                            id="ttsKeycloakClientSecret" 
                            type="password"
                            placeholder="Enter client secret"
                            value={ttsKeycloakClientSecret}
                            onChange={(e) => setTtsKeycloakClientSecret(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="ttsKeycloakAudience">TTS Keycloak Audience</Label>
                          <Input 
                            id="ttsKeycloakAudience" 
                            placeholder="my-tts-audience"
                            value={ttsKeycloakAudience}
                            onChange={(e) => setTtsKeycloakAudience(e.target.value)}
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="ttsModel">TTS Model</Label>
                  <Input 
                    id="ttsModel" 
                    placeholder="tts-1-hd"
                    value={ttsModel}
                    onChange={(e) => setTtsModel(e.target.value)}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="hostVoice">Host Voice</Label>
                    <Input 
                      id="hostVoice" 
                      placeholder="alloy"
                      value={hostVoice}
                      onChange={(e) => setHostVoice(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="guestVoice">Guest Voice</Label>
                    <Input 
                      id="guestVoice" 
                      placeholder="nova"
                      value={guestVoice}
                      onChange={(e) => setGuestVoice(e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDownloadModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleDownload}
              disabled={
                !inferenceUrl || 
                !apiKey || 
                !model ||
                ((activeDemoType === "multiagent-qna" || activeDemoType === "enterprise-rag-chatbot") && !embeddingModel) ||
                (activeDemoType === "pdf-to-podcast" && !useSameEndpointForTts && (!ttsApiKey || !ttsBaseUrl)) ||
                (activeDemoType === "enterprise-rag-chatbot" && !useLocalEmbeddings && !embeddingsBaseUrl)
              }
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              Download docker-compose.yml
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Terraform Ubuntu GenAI Installer Modal */}
      <Dialog open={terraformModalOpen} onOpenChange={setTerraformModalOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Generate Terraform main.tf for Ubuntu GenAI</DialogTitle>
            <DialogDescription>
              Fill in your iDRAC and Ubuntu settings. We will generate a main.tf that uses the public module VPC-DevAdmin/ubuntu-genai/idrac.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Configuration Mode */}
            <div className="space-y-3">
              <Label>Configuration mode</Label>
              <RadioGroup
                value={terraformForm.configMode}
                onValueChange={(value) => updateTerraformForm("configMode", value as "basic" | "advanced")}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="basic" id="config-basic" />
                  <Label htmlFor="config-basic" className="font-normal cursor-pointer">Basic (recommended)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="advanced" id="config-advanced" />
                  <Label htmlFor="config-advanced" className="font-normal cursor-pointer">Advanced</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Required Fields */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Required Settings</h3>
              
              <div className="space-y-2">
                <Label htmlFor="idracEndpoint">iDRAC Endpoint *</Label>
                <Input 
                  id="idracEndpoint" 
                  placeholder="https://10.0.0.5" 
                  value={terraformForm.idracEndpoint}
                  onChange={(e) => updateTerraformForm("idracEndpoint", e.target.value)}
                  className={terraformErrors.idracEndpoint ? "border-destructive" : ""}
                />
                <p className="text-xs text-muted-foreground">URL of your iDRAC Redfish endpoint. Must include https.</p>
                {terraformErrors.idracEndpoint && (
                  <p className="text-xs text-destructive">{terraformErrors.idracEndpoint}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="idracUser">iDRAC Username</Label>
                  <Input 
                    id="idracUser" 
                    value={terraformForm.idracUser}
                    onChange={(e) => updateTerraformForm("idracUser", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="idracPassword">iDRAC Password *</Label>
                  <Input 
                    id="idracPassword" 
                    type="password"
                    value={terraformForm.idracPassword}
                    onChange={(e) => updateTerraformForm("idracPassword", e.target.value)}
                    className={terraformErrors.idracPassword ? "border-destructive" : ""}
                  />
                  {terraformErrors.idracPassword && (
                    <p className="text-xs text-destructive">{terraformErrors.idracPassword}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ubuntuUsername">Ubuntu Username</Label>
                  <Input 
                    id="ubuntuUsername" 
                    value={terraformForm.ubuntuUsername}
                    onChange={(e) => updateTerraformForm("ubuntuUsername", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ubuntuPassword">Ubuntu Password *</Label>
                  <Input 
                    id="ubuntuPassword" 
                    type="password"
                    value={terraformForm.ubuntuPassword}
                    onChange={(e) => updateTerraformForm("ubuntuPassword", e.target.value)}
                    className={terraformErrors.ubuntuPassword ? "border-destructive" : ""}
                  />
                  {terraformErrors.ubuntuPassword && (
                    <p className="text-xs text-destructive">{terraformErrors.ubuntuPassword}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ubuntuHostname">Ubuntu Hostname</Label>
                <Input 
                  id="ubuntuHostname" 
                  value={terraformForm.ubuntuHostname}
                  onChange={(e) => updateTerraformForm("ubuntuHostname", e.target.value)}
                />
              </div>
            </div>

            {/* Advanced Fields */}
            {terraformForm.configMode === "advanced" && (
              <div className="space-y-4 pt-4 border-t">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Advanced Settings</h3>
                
                {/* Network Mode */}
                <div className="space-y-3">
                  <Label>Network Mode</Label>
                  <RadioGroup
                    value={terraformForm.useDhcp ? "dhcp" : "static"}
                    onValueChange={(value) => updateTerraformForm("useDhcp", value === "dhcp")}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="dhcp" id="network-dhcp" />
                      <Label htmlFor="network-dhcp" className="font-normal cursor-pointer">Use DHCP</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="static" id="network-static" />
                      <Label htmlFor="network-static" className="font-normal cursor-pointer">Use static IP</Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Static IP fields */}
                {!terraformForm.useDhcp && (
                  <div className="space-y-4 pl-4 border-l-2 border-muted">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="staticIp">Static IP *</Label>
                        <Input 
                          id="staticIp" 
                          placeholder="10.0.0.50"
                          value={terraformForm.staticIp}
                          onChange={(e) => updateTerraformForm("staticIp", e.target.value)}
                          className={terraformErrors.staticIp ? "border-destructive" : ""}
                        />
                        {terraformErrors.staticIp && (
                          <p className="text-xs text-destructive">{terraformErrors.staticIp}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="staticNetmask">Static Netmask (CIDR)</Label>
                        <Input 
                          id="staticNetmask" 
                          value={terraformForm.staticNetmask}
                          onChange={(e) => updateTerraformForm("staticNetmask", e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="staticGateway">Static Gateway *</Label>
                      <Input 
                        id="staticGateway" 
                        placeholder="10.0.0.1"
                        value={terraformForm.staticGateway}
                        onChange={(e) => updateTerraformForm("staticGateway", e.target.value)}
                        className={terraformErrors.staticGateway ? "border-destructive" : ""}
                      />
                      {terraformErrors.staticGateway && (
                        <p className="text-xs text-destructive">{terraformErrors.staticGateway}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="staticDns">Static DNS Servers</Label>
                      <Input 
                        id="staticDns" 
                        placeholder="10.0.0.10, 10.0.0.11"
                        value={terraformForm.staticDns}
                        onChange={(e) => updateTerraformForm("staticDns", e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">Comma-separated list of DNS servers</p>
                    </div>
                  </div>
                )}

                {/* SSH Keys */}
                <div className="space-y-2">
                  <Label htmlFor="sshKeys">SSH Public Keys</Label>
                  <Textarea 
                    id="sshKeys" 
                    placeholder="One SSH public key per line"
                    value={terraformForm.sshKeys}
                    onChange={(e) => updateTerraformForm("sshKeys", e.target.value)}
                    rows={3}
                  />
                  <p className="text-xs text-muted-foreground">Each line will be added as a separate SSH key</p>
                </div>

                {/* TLS Handling */}
                <div className="flex items-start space-x-3">
                  <Checkbox 
                    id="idracSslInsecure"
                    checked={terraformForm.idracSslInsecure}
                    onCheckedChange={(checked) => updateTerraformForm("idracSslInsecure", checked === true)}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="idracSslInsecure" className="cursor-pointer">Skip TLS verification for iDRAC</Label>
                    <p className="text-xs text-muted-foreground">Leave enabled for self-signed certificates. Uncheck only if iDRAC has a valid TLS certificate.</p>
                  </div>
                </div>

                {/* Module Version */}
                <div className="space-y-2">
                  <Label htmlFor="moduleVersion">Module Version</Label>
                  <Input 
                    id="moduleVersion" 
                    value={terraformForm.moduleVersion}
                    onChange={(e) => updateTerraformForm("moduleVersion", e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">Terraform module version. Defaults to 0.1.0.</p>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTerraformModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleTerraformDownload}
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              Download main.tf
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Downloads;
